import _ from 'lodash';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { NavAction } from './NavAction';
import { resolveNavAction } from './resolveNavAction';

type DispatchNavAction = (navAction: NavAction) => void;
const BatchedNavActionContext = createContext<DispatchNavAction | null>(null);

export function BatchedNavActionProvider({
	children,
}: {
	children: ReactNode;
}) {
	const location = useLocation();
	const navigate = useNavigate();

	const batch = useRef<NavAction[] | null>(null);

	const dispatchNavAction = useCallback(
		(navAction: NavAction) => {
			if (batch.current === null) {
				batch.current = [];
				setTimeout(() => {
					if (batch.current === null) {
						throw new Error(
							'Flush batch ran when batch was null. This should be impossible.'
						);
					}

					const { to, state, replace } = resolveNavAction(
						location,
						...batch.current
					);

					const currPath = _.pick(location, ['pathname', 'search', 'hash']);
					const isNewPath = _.isEqual(currPath, to);
					const hasState = state
						? _.isObject(state)
							? !_.isEmpty(state)
							: true
						: false;

					if (!isNewPath || hasState) {
						void navigate(to, { state, replace });
					}

					batch.current = null;
				}, 0);
			}

			batch.current.push(navAction);
		},
		[location, navigate]
	);

	return (
		<BatchedNavActionContext.Provider value={dispatchNavAction}>
			{children}
		</BatchedNavActionContext.Provider>
	);
}

/**
 * Use {@link LinkNavAction} instead of this where possible.
 *
 * Changes the current location when called. For example, this can be used to
 * redirect after a form submission.
 *
 * @see {@link NavAction}'s doc comment for example usage.
 */
export function useDispatchNavAction() {
	const dispatchNavAction = useContext(BatchedNavActionContext);

	if (!dispatchNavAction) {
		throw new Error(
			'useDispatchNavAction must be used within a BatchedNavActionProvider'
		);
	}

	return dispatchNavAction;
}
